import React from 'react';
import { Lock, ShieldAlert, Mail, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Shown when the logged-in client's playback_locked flag is true.
 * Renders FULL-SCREEN inside DJ Console — no music search, no queue, no playback.
 * Per spec (option 2a): "Full lockout — DJ Console page is replaced with a
 * 'Account pending verification — contact Super Admin to activate' screen."
 */
const PlaybackLockedScreen = ({ reason, onRecheck }) => (
  <div
    data-testid="playback-locked-screen"
    className="min-h-screen bg-black flex items-center justify-center px-6 py-12"
  >
    <div className="max-w-xl w-full">
      <div className="bg-white/[0.03] border border-[#FF007F]/30 rounded-3xl p-8 lg:p-10 backdrop-blur-xl shadow-[0_0_60px_rgba(255,0,127,0.15)]">
        <div className="flex items-center justify-center w-20 h-20 rounded-full bg-[#FF007F]/15 mx-auto mb-6 ring-1 ring-[#FF007F]/30">
          <Lock className="w-10 h-10 text-[#FF007F]" />
        </div>

        <h1
          className="text-3xl lg:text-4xl font-extrabold text-white text-center mb-3 tracking-wider"
          style={{ fontFamily: 'Outfit' }}
        >
          PLAYBACK LOCKED
        </h1>

        <p className="text-center text-gray-400 text-[15px] leading-relaxed mb-8">
          This venue&apos;s DJ Console is awaiting verification from your Super Admin
          before music playback can be activated.
        </p>

        {reason ? (
          <div className="bg-black/40 border border-white/10 rounded-2xl p-4 mb-8 flex items-start gap-3">
            <ShieldAlert className="w-4 h-4 text-[#FF8C00] flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FF8C00] mb-1">
                Reason
              </p>
              <p className="text-sm text-gray-300 leading-relaxed">{reason}</p>
            </div>
          </div>
        ) : null}

        <div className="space-y-3 mb-8">
          <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-gray-500">
            What to do next
          </p>
          <ol className="space-y-2 text-sm text-gray-400 leading-relaxed list-decimal list-inside">
            <li>
              Make sure the venue&apos;s <span className="text-white font-semibold">Apple
              Music</span> subscription is signed into this browser via{' '}
              <a
                href="/settings"
                className="text-[#FF007F] hover:underline font-semibold"
              >
                Settings → Connect Apple Music
              </a>.
            </li>
            <li>
              Contact your MusicBox Super Admin and let them know the venue is ready
              for activation.
            </li>
            <li>
              Once they unlock you, the DJ Console will load automatically — no need
              to re-login.
            </li>
          </ol>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={() => window.open('mailto:support@spinitup.local?subject=Playback Activation Request', '_blank')}
            className="flex-1 bg-[#FF007F] hover:bg-[#CC0066] text-white rounded-full font-bold tracking-wider h-12"
            data-testid="locked-contact-admin"
          >
            <Mail className="w-4 h-4 mr-2" /> CONTACT ADMIN
          </Button>
          <Button
            onClick={onRecheck}
            variant="ghost"
            className="border border-white/15 text-gray-300 hover:text-white hover:bg-white/5 rounded-full font-bold tracking-wider h-12"
            data-testid="locked-recheck"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> RECHECK STATUS
          </Button>
        </div>
      </div>

      <p className="text-center text-[10px] text-gray-600 mt-6 font-mono tracking-wider">
        MUSICBOX · PLAYBACK GATING · v1
      </p>
    </div>
  </div>
);

export default PlaybackLockedScreen;
